import {Footer} from "./footer";
import {Header} from "./header";
import {User} from "@sentry/browser";
import {useContext, useEffect, useState} from "preact/hooks";
import {Celsius, getTemperatureById, TemperatureContext} from "./generic/temperature";
import {getLocalStorageItem, setLocalStorageItem} from "../lib/localStorage";
import {ComponentChildren} from "preact";
import {GlobalDataContext} from "../app";
import {FetchContext, FetchProps} from "./fetchAndRender";
import styled, {useTheme} from "styled-components";
import {Card} from "./generic/card";

const BodyMain = styled.main`
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    margin-top: 1rem;
    
    &.container {
        align-self: center;
        width: 100%;
        max-width: 80rem;
        
        > ${Card} {
            margin-bottom: 1rem;
        }
    }
`;

type PageProps = {
    user?: User;
    links?: ComponentChildren;
    title?: string;
    showBackButton?: boolean;
    children?: ComponentChildren;
    class?: string;
    titleMarkup?: (title: string) => ComponentChildren;
};

export function Page(props: PageProps) {
    let initialTemperature: TemperatureContext = Celsius;
    try {
        initialTemperature = getTemperatureById(getLocalStorageItem<string>("temperature-unit", "celsius"));
    } catch (e) {
        console.error(e);
    }

    const [temperatureContext, setTemperatureContext] = useState<TemperatureContext>(initialTemperature);
    const switchTemperatureContext = (newCtx: TemperatureContext) => {
        setTemperatureContext(newCtx);
        setLocalStorageItem("temperature-unit", newCtx.id);
    }

    const [fetchContext, setFetchContext] = useState<FetchProps>({
        interval: Number.parseInt(getLocalStorageItem<string>("fetch-interval", "60")) ?? 60
    });

    const theme = useTheme();
    useEffect(() => {
        document.body.className = theme.bodyClass;
    }, [theme])

    const globalData = useContext(GlobalDataContext);
    if (!globalData) {
        return null;
    }

    return <TemperatureContext.Provider value={temperatureContext}>
        <FetchContext.Provider value={fetchContext}>
            <Header
                user={globalData.user}
                links={props.links}
                title={props.title}
                titleMarkup={props.titleMarkup}
                showBackButton={props.showBackButton}
            />
            <BodyMain className={props.class}>
                {props.children}
            </BodyMain>
            <Footer
                switchTemperatureContext={switchTemperatureContext}
                switchInterval={(newInterval: number) => setFetchContext(Object.assign({}, fetchContext, {interval: newInterval}))}
                version={globalData?.version}
            />
        </FetchContext.Provider>
    </TemperatureContext.Provider>
}
